/**
 * F32 (Wave 2C) — admin open-game detail page.
 *
 * Route: `/open-games/:id`
 * Permission: `open_games:read` gates the page render (route-level). The
 * Suspend / Unsuspend actions are additionally gated on `open_games:moderate`
 * (super_admin + content_moderator) — a `open_games:read`-only actor (support)
 * sees the read-only view with no action bar.
 *
 * Composition (D20 — admin can only view + suspend/unsuspend, never edit
 * spots/level/price):
 *   - Header with lifecycle StatusChip + suspended badge
 *   - Booking link (deep-link to /bookings/:bookingId) + venue + schedule
 *   - Participants Card
 *   - Report history Card
 *   - Suspend/Unsuspend action bar + dialogs (gated)
 *
 * Suspend posts to `POST /admin/open-games/:id/suspend` (with reason), unsuspend
 * to `POST /admin/open-games/:id/unsuspend`. Both audit-log server-side via
 * F06's onSend hook.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  useToast,
} from '@bravemove/ui-web';
import type { OpenGameReport, OpenGameStatus } from '@bravemove/types';
import { adminOpenGamesApi, type AdminOpenGameDetailResponse } from '../../api/openGames';
import { SuspendOpenGameDialog } from '../../components/open-games/SuspendOpenGameDialog';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';

const STATUS_CHIP_MAP: Record<OpenGameStatus, StatusChipStatus> = {
  open: 'approved',
  filled: 'submitted',
  closed: 'pending',
  cancelled: 'rejected',
};

const REPORT_STATUS_CHIP_MAP: Record<OpenGameReport['status'], StatusChipStatus> = {
  open: 'in_review',
  reviewed: 'approved',
  dismissed: 'pending',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminOpenGameDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<AdminOpenGameDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [unsuspendOpen, setUnsuspendOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [unsuspending, setUnsuspending] = useState(false);

  const canModerate = auth.adminUser?.permissions.includes('open_games:moderate') ?? false;

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const d = await adminOpenGamesApi.getGame(id);
      if (d === null) setNotFound(true);
      else setDetail(d);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const handleSuspend = async (reason: string): Promise<void> => {
    if (!id) return;
    setActionError(null);
    try {
      const game = await adminOpenGamesApi.suspendGame(id, { reason });
      setDetail((prev) => (prev ? { ...prev, game } : prev));
      setSuspendOpen(false);
      toast.push({
        tone: 'success',
        title: t('open-game:admin.detail.suspend', { defaultValue: 'Suspend' }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const handleUnsuspend = async (): Promise<void> => {
    if (!id) return;
    setActionError(null);
    setUnsuspending(true);
    try {
      const game = await adminOpenGamesApi.unsuspendGame(id);
      setDetail((prev) => (prev ? { ...prev, game } : prev));
      setUnsuspendOpen(false);
      toast.push({
        tone: 'success',
        title: t('open-game:admin.detail.unsuspend', { defaultValue: 'Unsuspend' }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    } finally {
      setUnsuspending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="open-game-detail-loading">
        <Skeleton className="h-7 w-1/3 rounded-md" />
        <Skeleton className="h-28 w-full rounded-lg" />
      </div>
    );
  }

  if (notFound) {
    return (
      <EmptyState
        title={t('open-game:error.GAME_NOT_FOUND', {
          defaultValue: 'This game no longer exists.',
        })}
      />
    );
  }

  if (error) {
    return <Banner tone="danger">{error}</Banner>;
  }

  if (!detail) return null;

  const { game, reports } = detail;

  return (
    <div className="space-y-6" data-testid="open-game-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-ink">
            {game.title ?? t('open-game:admin.detail.title', { defaultValue: 'Open game' })}
          </h1>
          <p className="text-[12px] text-ink-3 mt-0.5 font-mono">{game.id}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusChip
            status={STATUS_CHIP_MAP[game.status]}
            label={t(`open-game:status.${game.status}`)}
          />
          {game.suspended ? (
            <StatusChip
              status="rejected"
              label={t('open-game:admin.detail.suspended_badge', { defaultValue: 'Suspended' })}
              data-testid="open-game-suspended-badge"
            />
          ) : null}
        </div>
      </div>

      {/* Booking + venue + schedule */}
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-semibold text-ink-2 uppercase tracking-wide">
              {game.venueName}
            </span>
            <button
              type="button"
              className="text-[12px] text-accent hover:underline"
              onClick={() => navigate(`/bookings/${game.bookingId}`)}
              data-testid="open-game-booking-link"
            >
              {t('open-game:admin.detail.booking_link', { defaultValue: 'View booking' })}
            </button>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-3 capitalize">{game.sport.replace(/-/g, ' ')}</dt>
              <dd className="text-ink-2">{formatDate(game.startAt, locale)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-3">
                {t('open-game:post.spots_label', { defaultValue: 'Spots' })}
              </dt>
              <dd className="text-ink-2 font-mono">
                {game.spotsFilled}/{game.spotsTotal}
              </dd>
            </div>
          </dl>
        </div>
      </Card>

      {/* Participants */}
      <Card>
        <div className="flex flex-col gap-3">
          <h2 className="text-[13px] font-semibold text-ink-2 uppercase tracking-wide">
            {t('open-game:admin.detail.participants', { defaultValue: 'Players' })}
          </h2>
          {game.participants.length === 0 ? (
            <p className="text-[12.5px] text-ink-3">
              {t('open-game:detail.no_participants', {
                defaultValue: 'No players have joined yet.',
              })}
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5" role="list" data-testid="open-game-participants">
              {game.participants.map((p) => (
                <li
                  key={p.userId}
                  className="flex items-center justify-between gap-3 text-[12.5px]"
                >
                  <span className="text-ink">{p.displayName ?? p.userId.slice(0, 8)}</span>
                  <span className="text-ink-3">{p.role}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Report history — inline panel per F32 design canvas (D20). Each report
          shows: reason label + reporter user ID + filed-at timestamp + optional
          details text + status chip. No audit endpoint is available on the
          open-game detail response ({ game, reports }) so AuditLogTimeline is
          intentionally omitted here. */}
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-[13px] font-semibold text-ink-2 uppercase tracking-wide">
              {t('open-game:admin.detail.reports', { defaultValue: 'Reports' })}
            </h2>
            {reports.length > 0 ? (
              <span className="text-[11px] font-mono text-ink-3 tabular-nums">
                {reports.length}
              </span>
            ) : null}
          </div>
          {reports.length === 0 ? (
            <p className="text-[12.5px] text-ink-3">
              {t('open-game:admin.detail.no_reports', {
                defaultValue: 'No reports for this game.',
              })}
            </p>
          ) : (
            <ul className="flex flex-col gap-2" role="list" data-testid="open-game-reports">
              {reports.map((r) => (
                <li
                  key={r.id}
                  className="rounded-md border border-hairline bg-surface-2 p-3"
                  data-testid={`open-game-report-${r.id}`}
                >
                  {/* Row 1 — reason + status */}
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-[13px] font-medium text-ink">
                      {t(`open-game:report_reason.${r.reason}`)}
                    </span>
                    <StatusChip
                      size="sm"
                      status={REPORT_STATUS_CHIP_MAP[r.status]}
                      label={t(`open-game:admin.report_status.${r.status}`)}
                    />
                  </div>
                  {/* Row 2 — reporter + filed-at */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-ink-3">
                    <span>
                      {t('open-game:admin.detail.report_filed_by', {
                        defaultValue: 'Reporter',
                      })}{' '}
                      <span className="font-mono text-ink-2">{r.reporterUserId.slice(0, 8)}</span>
                    </span>
                    <span className="font-mono tabular-nums">
                      {formatDate(r.createdAt, locale)}
                    </span>
                  </div>
                  {/* Row 3 — optional details text */}
                  {r.details ? (
                    <p className="mt-1.5 text-[12px] text-ink-2 leading-snug">{r.details}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Moderation action bar — gated on open_games:moderate */}
      {canModerate ? (
        <div className="flex flex-col gap-2" data-testid="open-game-action-bar">
          {actionError ? <Banner tone="danger">{actionError}</Banner> : null}
          <div className="flex justify-end gap-2">
            {game.suspended ? (
              <Button
                variant="primary"
                onClick={() => setUnsuspendOpen(true)}
                data-testid="open-unsuspend"
              >
                {t('open-game:admin.detail.unsuspend', { defaultValue: 'Unsuspend' })}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setSuspendOpen(true)}
                data-testid="open-suspend"
              >
                {t('open-game:admin.detail.suspend', { defaultValue: 'Suspend' })}
              </Button>
            )}
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="text-[12px] text-accent hover:underline"
        onClick={() => navigate('/open-games')}
      >
        {t('open-game:marketplace.title', { defaultValue: 'Open games' })}
      </button>

      <SuspendOpenGameDialog
        open={suspendOpen}
        onSuspend={handleSuspend}
        onClose={() => setSuspendOpen(false)}
        errorMessage={suspendOpen ? actionError : null}
      />

      {/* Unsuspend confirmation */}
      {unsuspendOpen ? (
        <UnsuspendConfirm
          loading={unsuspending}
          onConfirm={() => void handleUnsuspend()}
          onClose={() => setUnsuspendOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** Small inline confirmation for the unsuspend action (no reason required). */
function UnsuspendConfirm({
  loading,
  onConfirm,
  onClose,
}: {
  readonly loading: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="unsuspend-dialog"
    >
      <div className="w-full max-w-sm rounded-lg bg-bg p-5 shadow-lg">
        <h2 className="text-[15px] font-semibold text-ink">
          {t('open-game:admin.unsuspend_dialog.title', { defaultValue: 'Unsuspend this game?' })}
        </h2>
        <p className="mt-2 text-[13px] text-ink-2">
          {t('open-game:admin.unsuspend_dialog.body', {
            defaultValue: 'It will become visible and joinable again.',
          })}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('open-game:admin.unsuspend_dialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={loading}
            data-testid="confirm-unsuspend"
          >
            {t('open-game:admin.unsuspend_dialog.confirm', { defaultValue: 'Unsuspend' })}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminOpenGameDetailPage;
